/**
 * useAutoSave — debounced persist of form state to the server.
 *
 * Fires PATCH /meetings/:id with the latest value `DEBOUNCE_MS` after
 * the last change. Exposes status so the UI can show "Saving…" /
 * "Saved Xs ago" / "Error" / "Conflict".
 *
 * Optimistic-concurrency support:
 *   Pass `version` (the server's last-known version for this row)
 *   and the hook includes it as `expectedVersion` in the PATCH body.
 *   If the server has moved on, the API returns 409 and the hook
 *   surfaces status.kind='conflict' so the UI can prompt the user
 *   to reload instead of silently overwriting someone else's edits.
 *
 * When a save succeeds, `onSaved(updated)` fires with the full
 * updated row — the caller updates their cached meeting (especially
 * its new `version`) so the next save sends the correct
 * expectedVersion.
 *
 * Won't trigger on the initial mount — only after the first user edit.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/apiClient.ts';

const DEBOUNCE_MS = 1500;

export type AutoSaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: Date }
  | { kind: 'conflict'; serverVersion: number }
  | { kind: 'error'; message: string };

export interface UseAutoSaveOptions<TSaved> {
  /** Last-known server version, sent as `expectedVersion` in each PATCH
   *  for optimistic concurrency. Omit to skip version checking. */
  version?: number;
  /** Fired after a successful PATCH. Receives the full updated row so
   *  the caller can refresh its cache (especially the new version). */
  onSaved?: (updated: TSaved) => void;
}

export function useAutoSave<TValue, TSaved = unknown>(
  meetingId: string | undefined,
  value: TValue,
  options?: UseAutoSaveOptions<TSaved>,
) {
  const [status, setStatus] = useState<AutoSaveStatus>({ kind: 'idle' });
  const isInitialRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef<TValue>(value);
  // Refs for version + callback so the timer-fired save always reads
  // the latest without re-subscribing effects.
  const versionRef = useRef<number | undefined>(options?.version);
  // Index into the type — `typeof options.onSaved` would dereference
  // a possibly-undefined `options` (TS18048). Indexing the parameter
  // type works whether options is provided or not.
  const onSavedRef = useRef<UseAutoSaveOptions<TSaved>['onSaved'] | undefined>(
    options?.onSaved,
  );

  // Keep refs fresh on every render.
  versionRef.current = options?.version;
  onSavedRef.current = options?.onSaved;

  useEffect(() => {
    latestValueRef.current = value;

    if (!meetingId) return;
    if (isInitialRef.current) {
      // First render — record the value but don't save it
      isInitialRef.current = false;
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      // expectedVersion is now REQUIRED by the api so the
      // PATCH UPDATE can use it as part of an atomic WHERE
      // clause for optimistic concurrency. If we don't have
      // a version yet (very first render before the meeting
      // query resolved), defer the save instead of firing a
      // doomed request — the next change-driven effect run
      // will retry once version arrives.
      if (typeof versionRef.current !== 'number') {
        setStatus({ kind: 'idle' });
        return;
      }
      setStatus({ kind: 'saving' });
      try {
        const body: Record<string, unknown> = {
          ...(latestValueRef.current as object),
          expectedVersion: versionRef.current,
        };
        const updated = await api.patch<TSaved>(
          `/meetings/${meetingId}`,
          body,
        );
        setStatus({ kind: 'saved', at: new Date() });
        onSavedRef.current?.(updated);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          // Extract the server version from the conflict response body
          // if available, otherwise fall back to the ref value.
          const data = err.data as { serverVersion?: number } | null;
          setStatus({
            kind: 'conflict',
            serverVersion: data?.serverVersion ?? versionRef.current ?? 0,
          });
          return;
        }
        const message =
          err instanceof ApiError ? err.message : 'Auto-save failed';
        setStatus({ kind: 'error', message });
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [meetingId, value]);

  return status;
}
